import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useLocation } from 'react-router-dom';
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Stack,
  useTheme,
  useMediaQuery,
  Typography,
  TextField,
  Divider,
} from '@mui/material';
import { complianceService } from '../../../app/services/api/compliance/complianceService';
import { ActionItem } from '../../../pages/Compliance/Interfaces/complicanceInterfaces';
import PageArea from '../../../components/shared/PageArea';
import PageTitle from '../../../components/shared/PageTitle';
import { UserContext } from '../../../components/shared/useUser';
import { Section } from '../../../components/ui/Section';
import MessagesSnackbar from '../../../components/shared/MessagesSnackbar';
import CustomBreadCrumbs from '../../../components/ui/CustomBreadCrumbs';
import { Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

const ActionItemDetailPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints.values.md}px)`);
  const { t } = useTranslation('complianceAudits');
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const { hasPermissionTo, selectedStore } = useContext(UserContext);

  const fromDashboard = (location.state as { from?: string })?.from === 'dashboard';
  const parentUrl = fromDashboard ? '/compliance/dashboard' : '/compliance/audits/action-items';
  const parentLabel = fromDashboard ? t('actionItems.complianceDashboard') : t('actionItems.actionItems');

  const canEdit = hasPermissionTo(['ActionItemsEdit']);
  const canClose = hasPermissionTo(['ActionItemsClose']);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [item, setItem] = useState<ActionItem | null>(null);

  // Resolve state
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [isResolving, setIsResolving] = useState(false);

  // Close state
  const [closeNotes, setCloseNotes] = useState('');
  const [isClosing, setIsClosing] = useState(false);

  // Snackbar
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  // Comment state
  const [newComment, setNewComment] = useState('');
  const [isAddingComment, setIsAddingComment] = useState(false);

  useEffect(() => {
    if (id) {
      loadActionItem();
    }
  }, [id, selectedStore?.storeNumber]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadActionItem = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await complianceService.actionItems.getById(Number(id), selectedStore?.storeNumber);
      setItem(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('actionItems.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleResolve = async () => {
    if (!item || resolutionNotes.trim().length < 50) return;
    try {
      setIsResolving(true);
      await complianceService.actionItems.resolve(item.id, resolutionNotes, selectedStore?.storeNumber);
      setSnackbarMessage(t('actionItemDetail.resolvedSuccess'));
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setResolutionNotes('');
      await loadActionItem();
    } catch (err) {
      setSnackbarMessage(err instanceof Error ? err.message : t('actionItems.failedToResolve'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsResolving(false);
    }
  };

  const handleClose = async () => {
    if (!item || closeNotes.trim().length < 50) return;
    try {
      setIsClosing(true);
      await complianceService.actionItems.close(item.id, selectedStore?.storeNumber, closeNotes);
      setSnackbarMessage(t('actionItemDetail.closedSuccess'));
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setCloseNotes('');
      await loadActionItem();
    } catch (err) {
      setSnackbarMessage(err instanceof Error ? err.message : t('actionItems.failedToClose'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsClosing(false);
    }
  };

  const handleAddComment = async () => {
    if (!item || !newComment.trim()) return;
    try {
      setIsAddingComment(true);
      const updated = await complianceService.actionItems.addFeedback(item.id, selectedStore?.storeNumber, newComment);
      setItem(updated);
      setNewComment('');
      setSnackbarMessage(t('actionItemDetail.commentAdded'));
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (err) {
      setSnackbarMessage(err instanceof Error ? err.message : t('actionItemDetail.commentFailed'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsAddingComment(false);
    }
  };

  const getPriorityColor = (priority: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (priority) {
      case 'Critical': return 'error';
      case 'High': return 'warning';
      case 'Medium': return 'info';
      case 'Low': return 'default';
      default: return 'default';
    }
  };

  const getStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (status) {
      case 'Open': return 'error';
      case 'InProgress': return 'warning';
      case 'Resolved': return 'success';
      case 'Closed': return 'default';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <PageArea>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </PageArea>
    );
  }

  if (error || !item) {
    return (
      <PageArea>
        <Alert
          severity="error"
          sx={{ mb: 5 }}
        >
          {error || t('actionItemDetail.notFound')}
        </Alert>
        <CustomBreadCrumbs title={t('actionItemDetail.backToList')}>
          <Link
            to={parentUrl}
            component={RouterLink}
          >
            {parentLabel}
          </Link>
        </CustomBreadCrumbs>
      </PageArea>
    );
  }

  const isOpenOrInProgress = item.status === 'Open' || item.status === 'InProgress';

  return (
    <PageArea>
      {/* Header */}
      <Stack
        width="100%"
        spacing={2}
        direction={isMobile ? 'column' : 'row'}
        sx={{ justifyContent: 'space-between', mb: 3 }}
      >
        <Box>
          <CustomBreadCrumbs title={t('actionItemDetail.pageTitle')}>
            <Link
              to={parentUrl}
              component={RouterLink}
            >
              {parentLabel}
            </Link>
          </CustomBreadCrumbs>
          <PageTitle>
            {t('actionItemDetail.pageTitle')}
          </PageTitle>
        </Box>
      </Stack>

      {/* Details Section */}
      <Section style={{ p: 5, mb: 5 }}>
        <Typography
          variant="textMD"
          fontWeight={600}
          sx={{ mb: 3 }}
        >
          {t('actionItemDetail.details')}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
          <Typography
            variant="textSM"
            color="text.secondary"
          >
            {t('actionItems.title')}
          </Typography>
          <Typography
            variant="textSM"
            fontWeight={500}
            sx={{ wordBreak: 'break-word' }}
          >
            {item.title}
          </Typography>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 3, mb: 3 }}>
          {/* Status & Priority */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography
              variant="textSM"
              color="text.secondary"
            >
              {t('common.status')}
            </Typography>
            <Chip
              label={item.status}
              color={getStatusColor(item.status)}
              size="small"
              sx={{ alignSelf: 'flex-start' }}
            />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography
              variant="textSM"
              color="text.secondary"
            >
              {t('common.priority')}
            </Typography>
            <Chip
              label={item.priority}
              color={getPriorityColor(item.priority)}
              size="small"
              sx={{ alignSelf: 'flex-start' }}
            />
          </Box>

          {/* Audit */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography
              variant="textSM"
              color="text.secondary"
            >
              {t('actionItemDetail.audit')}
            </Typography>
            {item.auditId && item.auditName ? (
              <Link
                to={`/compliance/audits/review/${item.auditId}`}
                component={RouterLink}
                underline="hover"
                variant="textSM"
                fontWeight={500}
              >
                {item.auditName}
              </Link>
            ) : (
              <Typography
                variant="textSM"
                fontWeight={500}
              >
                -
              </Typography>
            )}
          </Box>

          {/* Store & Assignee */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography
              variant="textSM"
              color="text.secondary"
            >
              {t('common.store')}
            </Typography>
            <Typography
              variant="textSM"
              fontWeight={500}
            >
              {item.storeName}
            </Typography>
          </Box>
          {item.assignedToUserName ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography
                variant="textSM"
                color="text.secondary"
              >
                {t('common.assignedTo')}
              </Typography>
              <Typography
                variant="textSM"
                fontWeight={500}
              >
                {item.assignedToUserName}
              </Typography>
            </Box>
          ) : null}

          {/* Dates */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography
              variant="textSM"
              color="text.secondary"
            >
              {t('common.dueDate')}
            </Typography>
            <Typography
              variant="textSM"
              fontWeight={500}
            >
              {item.dueDate ? new Date(item.dueDate).toLocaleDateString() : '-'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography
              variant="textSM"
              color="text.secondary"
            >
              {t('actionItemDetail.createdDate')}
            </Typography>
            <Typography
              variant="textSM"
              fontWeight={500}
            >
              {new Date(item.createdDate).toLocaleDateString()}
            </Typography>
          </Box>

          {/* Created by */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography
              variant="textSM"
              color="text.secondary"
            >
              {t('actionItemDetail.createdBy')}
            </Typography>
            <Typography
              variant="textSM"
              fontWeight={500}
            >
              {item.createdByUserName}
            </Typography>
          </Box>

          {/* Resolved Date */}
          {item.resolvedDate && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography
                variant="textSM"
                color="text.secondary"
              >
                {t('actionItemDetail.resolvedDate')}
              </Typography>
              <Typography
                variant="textSM"
                fontWeight={500}
              >
                {new Date(item.resolvedDate).toLocaleDateString()}
              </Typography>
            </Box>
          )}

          {/* Resolved By */}
          {item.resolvedBy && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography
                variant="textSM"
                color="text.secondary"
              >
                {t('actionItemDetail.resolvedBy')}
              </Typography>
              <Typography
                variant="textSM"
                fontWeight={500}
              >
                {item.resolvedBy}
              </Typography>
            </Box>
          )}

          {/* Closed Date */}
          {item.closedDate && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography
                variant="textSM"
                color="text.secondary"
              >
                {t('actionItemDetail.closedDate')}
              </Typography>
              <Typography
                variant="textSM"
                fontWeight={500}
              >
                {new Date(item.closedDate).toLocaleDateString()}
              </Typography>
            </Box>
          )}

          {/* Closed By */}
          {item.closedBy && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography
                variant="textSM"
                color="text.secondary"
              >
                {t('actionItemDetail.closedBy')}
              </Typography>
              <Typography
                variant="textSM"
                fontWeight={500}
              >
                {item.closedBy}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Description */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 3 }}>
          <Typography
            variant="textSM"
            color="text.secondary"
          >
            {t('common.description')}
          </Typography>
          <Typography variant="textSM" 
            sx={{ wordBreak: 'break-word' }}>
            {item.description}
          </Typography>
        </Box>

        {/* Auditor Notes */}
        {item.auditorNotes && (
          <Box sx={{ mt: 3 }}>
            <Typography
              variant="textSM"
              color="text.secondary"
            >
              {t('actionItemDetail.auditorNotes')}
            </Typography>
            <Box sx={{
              p: 2,
              bgcolor: theme.palette.custom.gray[50],
              borderRadius: 1,
              borderLeft: `3px solid ${theme.palette.info.main}`,
            }}>
              <Typography variant="textSM">
                {item.auditorNotes}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Resolution Notes */}
        {item.resolutionNotes && (
          <Box sx={{ mt: 3 }}>
            <Typography
              variant="textSM"
              color="text.secondary"
            >
              {t('actionItems.resolutionNotes')}
            </Typography>
            <Box sx={{
              p: 2,
              bgcolor: theme.palette.custom.gray[50],
              borderRadius: 1,
              borderLeft: `3px solid ${theme.palette.success.main}`,
            }}>
              {(item.resolvedDate || item.resolvedBy) && (
                <Typography
                  variant="textSM"
                  color="text.secondary"
                  fontWeight={500}
                  component="div"
                  sx={{ mb: 1, ml: 3 }}
                >
                  {item.resolvedDate && new Date(item.resolvedDate).toLocaleDateString()}
                  {item.resolvedBy && ` \u2014 ${item.resolvedBy}`}
                </Typography>
              )}
              <Typography
                variant="textSM"
                component="div"
                sx={{ ml: 3 }}
              >
                {item.resolutionNotes}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Closed Notes */}
        {item.closeNotes && (
          <Box sx={{ mt: 3 }}>
            <Typography
              variant="textSM"
              color="text.secondary"
            >
              {t('actionItemDetail.closeNotes')}
            </Typography>
            <Box sx={{
              p: 2,
              bgcolor: theme.palette.custom.gray[50],
              borderRadius: 1,
              borderLeft: `3px solid ${theme.palette.warning.main}`,
            }}>
              {(item.closedDate || item.closedBy) && (
                <Typography
                  variant="textSM"
                  color="text.secondary"
                  fontWeight={500}
                  component="div"
                  sx={{ mb: 1, ml: 3 }}
                >
                  {item.closedDate && new Date(item.closedDate).toLocaleDateString()}
                  {item.closedBy && ` \u2014 ${item.closedBy}`}
                </Typography>
              )}
              <Typography
                variant="textSM"
                component="div"
                sx={{ ml: 3 }}
              >
                {item.closeNotes}
              </Typography>
            </Box>
          </Box>
        )}
      </Section>

      {/* Comments Section */}
      <Section style={{ p: 5, mb: 5 }}>
        <Typography
          variant="textMD"
          fontWeight={600}
          sx={{ mb: 3 }}
        >
          {t('actionItemDetail.comments')}
        </Typography>

        {/* Comment Input */}
        <Box sx={{ display: 'flex', gap: 4, mb: 3 }}>
          <TextField
            sx={{ mt: 3 }}
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder={t('actionItemDetail.addCommentPlaceholder')}
            multiline
            minRows={2}
            maxRows={4}
            fullWidth
            size="small"
          />
          <Button
            variant="primary"
            size="md"
            onClick={handleAddComment}
            disabled={isAddingComment || !newComment.trim()}
            sx={{ alignSelf: 'center', whiteSpace: 'nowrap' }}
          >
            {t('actionItemDetail.addComment')}
          </Button>
        </Box>

        {/* Comment Thread */}
        {item.comments && item.comments.length > 0 ? (
          <Stack spacing={2}>
            {item.comments.map((comment) => (
              <Box
                key={comment.id}
                sx={{
                  display: 'flex',
                  gap: 4,
                  p: 2,
                  bgcolor: theme.palette.custom.gray[50],
                  borderRadius: 1,
                }}
              >
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    fontSize: '0.875rem',
                    bgcolor: theme.palette.primary.main,
                  }}
                >
                  {comment.createdByUserName?.charAt(0)?.toUpperCase() || '?'}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack
                    direction="row"
                    spacing={3}
                    alignItems="center"
                    sx={{ mb: 1 }}
                  >
                    <Typography
                      variant="textSM"
                      fontWeight={600}
                    >
                      {comment.createdByUserName}
                    </Typography>
                    <Typography
                      variant="textSM"
                      color="text.secondary"
                    >
                      {new Date(comment.createdDate).toLocaleString()}
                    </Typography>
                  </Stack>
                  <Typography
                    variant="textSM"
                    sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                  >
                    {comment.comment}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        ) : (
          <Typography
            variant="textSM"
            color="text.secondary"
          >
            {t('actionItemDetail.noComments')}
          </Typography>
        )}
      </Section>

      {/* Actions Section - Resolve / Close */}
      {isOpenOrInProgress && canEdit && (
        <Section style={{ p: 5, mb: 5 }}>
          <Typography
            variant="textMD"
            fontWeight={600}
            sx={{ mb: 3 }}
          >
            {t('actionItemDetail.actions')}
          </Typography>

          {/* Resolve */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="textSM"
              fontWeight={600}
              sx={{ mb: 2 }}
            >
              {t('actionItems.resolveActionItem')}
            </Typography>
            <TextField
              sx={{ mt: 3 }}
              label={t('actionItems.resolutionNotes')}
              value={resolutionNotes}
              onChange={(e) => setResolutionNotes(e.target.value)}
              multiline
              minRows={3}
              maxRows={8}
              fullWidth
              required
              error={resolutionNotes.trim().length > 0 && resolutionNotes.trim().length < 50}
              helperText={
                resolutionNotes.trim().length > 0 && resolutionNotes.trim().length < 50
                  ? t('actionItems.minCharactersRequired', { count: 50, current: resolutionNotes.trim().length })
                  : `${resolutionNotes.trim().length} / 50 ${t('actionItems.minCharacters')}`
              }
            />
            <Button
              variant="primary"
              color="success"
              size="lg"
              onClick={handleResolve}
              disabled={isResolving || !resolutionNotes.trim() || resolutionNotes.trim().length < 50}
              sx={{ mt: 2 }}
            >
              {t('actionItems.markAsResolved')}
            </Button>
          </Box>

          <Divider sx={{ my: 5 }} />

          {/* Close */}
          {canClose && (
            <Box>
              <Typography
                variant="textSM"
                fontWeight={600}
                sx={{ mb: 2 }}
              >
                {t('actionItems.closeActionItem')}
              </Typography>
              <Alert
                severity="info"
                sx={{ mt: 1, mb: 5 }}
              >
                {t('actionItems.closeInfo')}
              </Alert>
              <TextField
                label={t('actionItems.closeNotes')}
                value={closeNotes}
                onChange={(e) => setCloseNotes(e.target.value)}
                multiline
                minRows={3}
                maxRows={8}
                fullWidth
                required
                error={closeNotes.trim().length > 0 && closeNotes.trim().length < 50}
                helperText={
                  closeNotes.trim().length > 0 && closeNotes.trim().length < 50
                    ? t('actionItems.minCharactersRequired', { count: 50, current: closeNotes.trim().length })
                    : `${closeNotes.trim().length} / 50 ${t('actionItems.minCharacters')}`
                }
              />
              <Button
                variant="primary"
                color="warning"
                size="lg"
                onClick={handleClose}
                disabled={isClosing || !closeNotes.trim() || closeNotes.trim().length < 50}
                sx={{ mt: 2 }}
              >
                {t('actionItems.markAsClosed')}
              </Button>
            </Box>
          )}
        </Section>
      )}

      <MessagesSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        duration={3000}
        onClose={() => setSnackbarOpen(false)}
      />
    </PageArea>
  );
};

export default ActionItemDetailPage;
